import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import ArtworkCard from "@/components/ArtworkCard";
import CountUp from "@/components/CountUp";
import { BrandMark } from "@/components/Navbar";
import HeroVideo from "@/components/HeroVideo";
import TruthOfTheWeek from "@/components/TruthOfTheWeek";
import TruthOfTheDay from "@/components/TruthOfTheDay";
import WhatDoYouSeePromo from "@/components/WhatDoYouSeePromo";
import MarkerCollectionSpotlight from "@/components/MarkerCollectionSpotlight";
import {
  ArrowRight, BookOpen, QrCode, Package, Heart, ShieldCheck, ScanLine,
  ShoppingCart, Send, Users, CheckCircle2, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";

// Module-level cache so subsequent navigations to Home don't flash either
let _contentCache = null;
let _contentInflight = null;

export default function Home() {
  useSEO("home");
  const [arts, setArts] = useState([]);
  const [stats, setStats] = useState({
    members: 1842, pieces_released: 7, qr_scans: 12395, pieces_sold: 3217, collection_goal: 52
  });
  const [email, setEmail] = useState("");
  const [sections, setSections] = useState([]);
  const [content, setContent] = useState(() => _contentCache || {});
  const [contentReady, setContentReady] = useState(() => _contentCache !== null);
  const [collectors, setCollectors] = useState([]);
  const [hideArt, setHideArt] = useState(false);

  useEffect(() => {
    http.get("/artworks").then(({ data }) => setArts(data)).catch(() => {});
    http.get("/public/stats").then(({ data }) => setStats(prev => ({ ...prev, ...data }))).catch(() => {});
    http.get("/public/sections").then(({ data }) => setSections(data)).catch(() => {});
    http.get("/public/founding-collectors").then(({ data }) => setCollectors(data.collectors || [])).catch(() => {});
    http.get("/public/settings").then(({ data }) => setHideArt(!!data?.hide_art_collection)).catch(() => {});

    if (_contentCache !== null) return;
    if (!_contentInflight) {
      _contentInflight = http.get("/public/content")
        .then(({ data }) => { _contentCache = data || {}; return _contentCache; })
        .catch(() => { _contentCache = {}; return _contentCache; });
    }
    _contentInflight.then(c => { setContent(c); setContentReady(true); });
  }, []);

  // helpers
  const sectionEnabled = (key) => {
    if (!sections.length) return true; // fallback render-all if not loaded
    const s = sections.find(x => x.key === key);
    return s ? s.enabled !== false : true;
  };
  const baseOrder = sections.length
    ? sections.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(s => s.key)
    : ["hero", "truth_of_day", "truth_of_week", "wdys_promo", "featured", "truth_wall_preview", "feature_strip", "truth_markers_preview", "two_band", "stats", "founding52"];
  // Always inject WDYS promo + Truth Of The Week right after the hero so they don't
  // disappear when an admin saved a sections config before these existed.
  const ensured = [...baseOrder];
  const ensureAfter = (key, anchor) => {
    if (!ensured.includes(key)) {
      const i = ensured.indexOf(anchor);
      ensured.splice(i >= 0 ? i + 1 : 0, 0, key);
    }
  };
  ensureAfter("truth_of_week", "hero");
  ensureAfter("wdys_promo", "truth_of_week");
  ensureAfter("truth_of_day", "hero");
  ensureAfter("truth_wall_preview", "featured");
  ensureAfter("marker_collection_spotlight", "truth_wall_preview");
  ensureAfter("truth_markers_preview", "feature_strip");
  const orderedKeys = ensured;
  const c = (key, field, fallback) => content[`section_${key}`]?.[field] || fallback;
  const ml = (s) => (s || "").replace(/\\n/g, "\n");

  const featured = arts.filter(a => a.featured).slice(0, 4);
  const heroArt = arts.find(a => a.slug?.startsWith("the-crown")) || featured[0] || arts[0];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await http.post("/newsletter", { email }); toast.success("You're in. Watch your inbox."); setEmail(""); }
    catch { toast.error("Could not subscribe."); }
  };

  return (
    <Layout>
      {orderedKeys.map(key => {
        if (!sectionEnabled(key)) return null;
        if (key === "hero") return (
          <section key="hero" data-testid="hero-section" className="relative bg-[#050505] overflow-hidden">
            <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-12 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-4 fade-up">
                <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-5">{c("hero", "eyebrow", "Welcome To")}</div>
                <h1 className="font-display text-[#F4F1EA] text-[56px] sm:text-[72px] md:text-[88px] leading-[0.92] whitespace-pre-line">
                  {ml(c("hero", "headline", "THE\nSIMPLE\nTRUTHS\nCLUB"))}
                </h1>
                <p className="font-script text-[#C9A055] text-[32px] md:text-[36px] mt-6 leading-tight whitespace-pre-line">
                  {ml(c("hero", "script", "Sometimes a single reminder\ncan change everything."))}
                </p>
                <p className="font-body text-[15px] text-[#B5B0A7] mt-7 max-w-md leading-relaxed whitespace-pre-line">
                  {ml(c("hero", "body", "52 meaningful pieces. 52 simple truths. One growing collection designed to make you stop, think, and remember what matters."))}
                </p>
                <div className="flex flex-wrap gap-3 mt-9">
                  {!hideArt ? (
                    <>
                      <Link to={c("hero", "cta1_link", "/shop")} data-testid="hero-shop-cta" className="btn-gold">{c("hero", "cta1_label", "Explore the Collection")}</Link>
                      <Link to={c("hero", "cta2_link", "/subscriptions")} data-testid="hero-join-cta" className="btn-outline">{c("hero", "cta2_label", "Join the Club")}</Link>
                    </>
                  ) : (
                    <Link to="/marker-collection" data-testid="hero-shop-cta" className="btn-gold">Explore the Marker Collection</Link>
                  )}
                </div>
              </div>
              {(c("hero", "hero_video", "") || c("hero", "hero_image", "") || heroArt) && (
                <div className="lg:col-span-5 fade-up-delay-1 order-first lg:order-none">
                  {c("hero", "hero_video", "") ? (
                    contentReady && (
                      <HeroVideo
                        src={c("hero", "hero_video", "")}
                        poster={c("hero", "hero_video_poster", "")}
                        fallbackImg={c("hero", "hero_image", "") || heroArt?.main_image}
                        alt="Welcome to Simple Truths Club"
                      />
                    )
                  ) : (
                    <Link to={heroArt ? (hideArt ? "/marker-collection" : `/artwork/${heroArt.slug}`) : (hideArt ? "/marker-collection" : "/shop")} className="relative block aspect-[4/5] max-w-[520px] mx-auto overflow-hidden border border-white/5 bg-[#0a0a0a]">
                      {contentReady && (
                        <img
                          src={c("hero", "hero_image", "") ? resolveImage(c("hero", "hero_image", "")) : resolveImage(heroArt?.main_image)}
                          alt={heroArt ? heroArt.title : "Simple Truths Club"}
                          className="w-full h-full object-cover kenburns"
                          data-testid="hero-image"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
                    </Link>
                  )}
                </div>
              )}
              <div className="lg:col-span-3 fade-up-delay-2 space-y-10">
                <div>
                  <div className="text-[#C9A055] text-5xl font-serif leading-none mb-3 select-none">“</div>
                  <p className="font-body text-[#F4F1EA] text-lg md:text-xl leading-snug whitespace-pre-line">
                    {ml(c("hero", "quote", "Art with a story.\nTruths you can\ncarry with you."))}
                  </p>
                  <p className="font-script text-[#C9A055] text-2xl mt-4">{c("hero", "signature", "— Mickey Trivett")}</p>
                </div>
                <div className="border-t border-[#C9A055]/30 pt-7">
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#B5B0A7] mb-2">The Collection Goal</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-display text-[#C9A055] text-6xl md:text-7xl"><CountUp end={stats.pieces_released} /></span>
                    <span className="font-display text-[#B5B0A7] text-3xl md:text-4xl">/ {stats.collection_goal}</span>
                  </div>
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#F4F1EA] mb-5">Pieces Released</div>
                  <Link to={hideArt ? "/marker-collection" : "/shop"} data-testid="hero-collection-link" className="btn-outline text-[10px] px-6 py-3">{hideArt ? "View Marker Collection" : "View the Collection"}</Link>
                </div>
              </div>
            </div>
          </section>
        );

        if (key === "mirror_conversations_promo") return (
          <section key="mirror_conversations_promo" data-testid="mirror-conversations-promo" className="bg-[#1A1918] relative py-32 px-6 overflow-hidden border-b border-white/5">
             <div className="absolute inset-0 z-0">
                <img src={resolveImage("https://customer-assets.emergentagent.com/job_emotional-art-1/artifacts/9zkjns0s_IMG_9960.jpeg")} alt="Mirror" className="w-full h-full object-cover object-center opacity-40 mix-blend-luminosity grayscale" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#1A1918] via-[#1A1918]/90 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918] via-transparent to-[#1A1918]/80"></div>
             </div>
             <div className="max-w-[1400px] mx-auto w-full relative z-10 flex flex-col items-start text-left">
                <div className="font-ui text-[#D4AF37] tracking-[0.3em] uppercase text-[10px] md:text-xs mb-6">
                  NEW SERVICE
                </div>
                <h2 className="font-mirror-heading text-5xl md:text-7xl lg:text-8xl leading-[0.9] text-[#F9F6F0] mb-6 uppercase tracking-tight" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                  MIRROR<br/>CONVERSATIONS
                </h2>
                <p className="font-mirror-body text-[#D1CFC7] text-xl md:text-3xl italic leading-relaxed mb-10 max-w-xl" style={{fontFamily: "'Outfit', sans-serif"}}>
                  A mirror doesn’t tell you who you are.<br/>It simply reflects what has always been there.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/mirror-conversations" className="bg-[#D4AF37] text-black px-8 py-4 text-[11px] font-ui tracking-[0.2em] uppercase text-center hover:bg-[#E5C86C] transition-colors font-bold">
                    Book a Conversation
                  </Link>
                  <Link to="/mirror-conversations#what-is-it" className="border border-[#D4AF37]/50 text-[#D4AF37] px-8 py-4 text-[11px] font-ui tracking-[0.2em] uppercase text-center hover:bg-[#D4AF37]/10 transition-colors">
                    What is this?
                  </Link>
                </div>
             </div>
          </section>
        );

        if (key === "truth_of_week") return <TruthOfTheWeek key="truth_of_week"/>;
        if (key === "truth_of_day") return <TruthOfTheDay key="truth_of_day" c={c} />;
        if (key === "wdys_promo") return <WhatDoYouSeePromo key="wdys_promo"/>;

        if (key === "truth_wall_preview") return (
          <section key="truth_wall_preview" data-testid="truth-wall-preview"
                   className="bg-[#0a0a0a] py-16 sm:py-24 border-y border-white/10">
            <div className="max-w-4xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
                  {c("truth_wall_preview", "eyebrow", "The Truth Wall")}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] leading-[1.1] mb-5">
                  {c("truth_wall_preview", "title", "Share the truth life taught you.")}
                </h2>
                <p className="font-body text-[15px] leading-[1.85] text-[#B5B0A7] mb-7">
                  {c("truth_wall_preview", "body",
                    "The Truth Wall is where ordinary people share extraordinary lessons. Submit yours. Vote on others. Help shape future Simple Truths.")}
                </p>
                <Link to={c("truth_wall_preview", "cta_link", "/truth-wall")}
                      className="btn-gold inline-block"
                      data-testid="truth-wall-preview-cta">
                  {c("truth_wall_preview", "cta_label", "Visit the Truth Wall")}
                </Link>
              </div>
              <div className="bg-[#050505] border border-white/10 p-8 relative">
                <div className="absolute -top-3 left-6 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.22em] uppercase px-3 py-1">
                  Recent
                </div>
                <div className="space-y-5 mt-3">
                  {(c("truth_wall_preview", "samples_raw", "") || "I wish I'd worried less.\nNobody remembers how busy you were.\nThe right people stay.")
                    .split("\n").filter(Boolean).slice(0, 3).map((s, i) => (
                    <div key={i} className="font-display text-[#F4F1EA] text-lg leading-snug pl-5 border-l-2 border-[#C9A055]/40">
                      &ldquo;{s}&rdquo;
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );

        if (key === "marker_collection_spotlight") return <MarkerCollectionSpotlight key="marker_collection_spotlight" />;

        if (key === "truth_markers_preview") return (
          <section key="truth_markers_preview" data-testid="truth-markers-preview"
                   className="bg-[#050505] py-16 sm:py-24 border-y border-white/10">
            <div className="max-w-4xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
              <div className="bg-[#0a0a0a] border border-white/10 aspect-[5/4] flex items-center justify-center p-10 relative">
                <div className="text-center">
                  <div className="text-[#C9A055] text-6xl font-display leading-none">📍</div>
                  <div className="font-display text-[#F4F1EA] text-xl mt-4 mb-1">
                    {c("truth_markers_preview", "card_truth", '"Sit. Somebody would give anything for one more conversation."')}
                  </div>
                  <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-3">
                    {c("truth_markers_preview", "card_location", "On a park bench · Nashville")}
                  </div>
                </div>
              </div>
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
                  {c("truth_markers_preview", "eyebrow", "Truth Markers")}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] leading-[1.1] mb-5">
                  {c("truth_markers_preview", "title", "Take the truth into the world.")}
                </h2>
                <p className="font-body text-[15px] leading-[1.85] text-[#B5B0A7] mb-7">
                  {c("truth_markers_preview", "body",
                    "Truth Markers are small reminders placed with purpose. Stickers, signs, QR codes, and stories that turn everyday places into moments of reflection.")}
                </p>
                <Link to={c("truth_markers_preview", "cta_link", "/truth-markers")}
                      className="btn-gold inline-block"
                      data-testid="truth-markers-preview-cta">
                  {c("truth_markers_preview", "cta_label", "Explore Truth Markers")}
                </Link>
              </div>
            </div>
          </section>
        );

        if (key === "featured") {
          if (hideArt) return null; // Hide featured art section when art collection is off
          return (
          <section key="featured" className="bg-[#050505] py-16 md:py-24 border-t border-white/5" data-testid="featured-section">
            <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl md:text-5xl text-[#F4F1EA] tracking-wide">{c("featured", "title", "FEATURED TRUTHS")}</h2>
                <p className="font-script text-[#C9A055] text-2xl md:text-3xl mt-3">{c("featured", "subtitle", "Every piece has a meaning. Every story matters.")}</p>
                <div className="mt-5"><Link to="/shop" className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] hover:text-[#E0BC72] inline-flex items-center gap-2">View All Collection <ArrowRight size={13} /></Link></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {featured.map((a, i) => <ArtworkCard art={a} key={a.id} index={i} />)}
              </div>
            </div>
          </section>
          );
        }

        if (key === "feature_strip") {
          const defaults = [
            { i: <BookOpen size={28}/>, t: "Meaningful Art", d: "Every piece is created with a story and a purpose." },
            { i: <QrCode size={28}/>, t: "QR Gallery", d: "Scan. Discover the story. Own the art. Anywhere." },
            { i: <Package size={28}/>, t: "Quality & Care", d: "Premium prints, secure packaging, shipped with care." },
            { i: <Heart size={28}/>, t: "Built for Impact", d: "More than art. It's a movement of reminders." },
            { i: <ShieldCheck size={28}/>, t: "Secure Checkout", d: "Powered by Stripe. Safe. Secure. Trusted." },
          ];
          return (
            <section key="feature_strip" className="border-y border-white/10 bg-[#050505]">
              <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-5 gap-y-8 py-12">
                {defaults.map((f, i) => (
                  <div key={i} className="flex flex-col items-center text-center px-4">
                    <div className="text-[#C9A055] mb-4">{f.i}</div>
                    <div className="font-ui text-[12px] tracking-[0.28em] text-[#C9A055] mb-2.5">{c("feature_strip", `f${i+1}_title`, f.t)}</div>
                    <p className="font-body text-[13px] text-[#B5B0A7] leading-relaxed max-w-[200px]">{c("feature_strip", `f${i+1}_desc`, f.d)}</p>
                  </div>
                ))}
              </div>
            </section>
          );
        }

        if (key === "two_band") return (
          <section key="two_band" className="bg-[#050505] py-16 md:py-20">
            <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-6">
              <div className="relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#1a1814] via-[#0a0a0a] to-black">
                <div className="grid grid-cols-2 gap-6 items-center p-8 md:p-10 min-h-[320px]">
                  <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-black flex items-center justify-center">
                    <div className="text-center p-6">
                      <div className="bg-white p-4 inline-block mb-3"><ScanLine size={48} className="text-black" /></div>
                      <div className="font-ui text-[9px] tracking-[0.32em] text-[#F4F1EA]">SCAN TO<br/>DISCOVER<br/>THE STORY</div>
                    </div>
                  </div>
                  <div>
                    <div className="gold-eyebrow mb-3">{c("two_band", "band1_eyebrow", "QR Gallery")}</div>
                    <h3 className="font-display text-3xl md:text-[40px] text-[#F4F1EA] leading-tight mb-4 whitespace-pre-line">{ml(c("two_band", "band1_headline", "ART IN THE\nREAL WORLD"))}</h3>
                    <p className="font-body text-[14px] text-[#B5B0A7] leading-relaxed mb-6 whitespace-pre-line">{ml(c("two_band", "band1_body", "Our QR Gallery program brings Simple Truths to local businesses, events, and public spaces.\nScan the code. Read the story. Take the truth with you."))}</p>
                    <Link to={c("two_band", "band1_cta_link", "/mobile-gallery")} data-testid="qr-learn-more-cta" className="btn-gold">{c("two_band", "band1_cta_label", "Learn More")}</Link>
                  </div>
                </div>
              </div>
              <div className="relative overflow-hidden border border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] via-[#0a0a0a] to-black">
                <div className="grid grid-cols-2 gap-6 items-center p-8 md:p-10 min-h-[320px]">
                  <div>
                    <div className="gold-eyebrow mb-3">{c("two_band", "band2_eyebrow", "Join The Simple Truths Club")}</div>
                    <h3 className="font-display text-3xl md:text-[40px] text-[#F4F1EA] leading-tight mb-4 whitespace-pre-line">{ml(c("two_band", "band2_headline", "GET NEW TRUTHS.\nDELIVERED."))}</h3>
                    <p className="font-body text-[14px] text-[#B5B0A7] leading-relaxed mb-5 whitespace-pre-line">{ml(c("two_band", "band2_body", "Be the first to receive new releases, exclusive stories, and members-only content straight to your inbox."))}</p>
                    <form onSubmit={subscribe} className="flex" data-testid="home-newsletter-form">
                      <input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="Enter your email" className="stc-input flex-1 rounded-r-none" data-testid="home-newsletter-email"/>
                      <button type="submit" data-testid="home-newsletter-submit" className="btn-gold rounded-l-none px-5 text-[10px]">Join the Club</button>
                    </form>
                  </div>
                  <div className="aspect-[3/4] border border-[#C9A055]/20 bg-[#1a1814] flex items-center justify-center relative overflow-hidden">
                    <div className="text-center"><BrandMark size="sm" /></div>
                    <div className="absolute inset-0 opacity-5 pointer-events-none" style={{backgroundImage: "repeating-linear-gradient(45deg, #C9A055 0, #C9A055 1px, transparent 1px, transparent 22px)"}}/>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );

        if (key === "stats") {
          const items = [
            { i: <Users size={28}/>, label: c("stats", "s1_label", "Collecting Truths"), val: stats.members, suffix: "+", sub: c("stats", "s1_sub", "Members") },
            { i: <BookOpen size={28}/>, label: c("stats", "s2_label", "Artworks Released"), val: stats.pieces_released, suffix: ` / ${stats.collection_goal}`, sub: c("stats", "s2_sub", "And Growing") },
            { i: <ScanLine size={28}/>, label: c("stats", "s3_label", "QR Scans"), val: stats.qr_scans, suffix: "+", sub: c("stats", "s3_sub", "And Counting") },
            { i: <ShoppingCart size={28}/>, label: c("stats", "s4_label", "Pieces Sold"), val: stats.pieces_sold, suffix: "+", sub: c("stats", "s4_sub", "Thank You") },
          ];
          return (
            <section key="stats" className="bg-[#050505] py-14 md:py-20 border-t border-white/10">
              <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="public-stats-row">
                {items.map((s, i) => (
                  <div key={i} className="flex gap-4 items-start" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="text-[#C9A055] mt-1">{s.i}</div>
                    <div>
                      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1.5">{s.label}</div>
                      <div className="font-display text-3xl md:text-4xl text-[#C9A055] leading-none"><CountUp end={s.val} /><span className="text-[#B5B0A7]">{s.suffix}</span></div>
                      <div className="font-ui text-[10px] tracking-[0.32em] text-[#F4F1EA] mt-1.5">{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        }
        if (key === "founding52") {
          const goal = stats.collection_goal || 52;
          const filled = Math.min(collectors.length, goal);
          const slots = Array.from({ length: goal }).map((_, i) => collectors[i] || null);
          return (
            <section key="founding52" data-testid="founding52-section" className="bg-[#050505] py-16 md:py-24 border-t border-white/10">
              <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
                <div className="text-center mb-12">
                  <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-3">{c("founding52", "eyebrow", "Founding 52")}</div>
                  <h2 className="font-display text-3xl md:text-5xl text-[#F4F1EA] tracking-wide">{c("founding52", "title", "THE FOUNDING COLLECTORS")}</h2>
                  <p className="font-script text-[#C9A055] text-2xl md:text-3xl mt-3">{c("founding52", "subtitle", "The first 52 to carry a truth.")}</p>
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#B5B0A7] mt-5">
                    <span className="text-[#C9A055]">{filled}</span> / {goal} spots claimed
                  </div>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-13 gap-2 md:gap-3 max-w-[1280px] mx-auto" style={{gridTemplateColumns: "repeat(13, minmax(0, 1fr))"}}>
                  {slots.map((collector, i) => (
                    <div
                      key={i}
                      data-testid={`founding52-slot-${i}`}
                      className={`relative aspect-square border flex flex-col items-center justify-center p-2 transition-all ${
                        collector
                          ? "border-[#C9A055]/50 bg-[#C9A055]/10 hover:bg-[#C9A055]/20"
                          : "border-white/10 bg-[#0a0a0a]"
                      }`}
                      title={collector ? `${collector.name} — ${collector.first_piece}` : "Open spot"}
                    >
                      <div className={`font-ui text-[8px] tracking-[0.12em] mb-0.5 ${collector ? "text-[#C9A055]" : "text-[#8a857d]"}`}>
                        #{String(i + 1).padStart(2, "0")}
                      </div>
                      {collector ? (
                        <>
                          <div className="font-display text-[11px] md:text-[13px] text-[#F4F1EA] leading-tight text-center break-all">
                            {collector.name.toUpperCase()}
                          </div>
                        </>
                      ) : (
                        <div className="font-ui text-[8px] tracking-[0.2em] text-[#6a6660]">OPEN</div>
                      )}
                    </div>
                  ))}
                </div>

                <p className="font-body italic text-center text-[#B5B0A7] mt-10 max-w-xl mx-auto">
                  {c("founding52", "footer", "Be one of the first 52. Your name belongs here.")}
                </p>
                <div className="text-center mt-4">
                  <Link to="/shop" className="btn-gold">Claim Your Spot</Link>
                </div>
              </div>
            </section>
          );
        }
        return null;
      })}
    </Layout>
  );
}
